import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInstagram } from '../../services/instagram';
import { Spinner } from '../common/Spinner';

interface InstagramCallbackProps {
  onSuccess?: (data: any) => void;
}

export const InstagramCallback: React.FC<InstagramCallbackProps> = ({ onSuccess }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleCallback } = useInstagram();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'no-instagram'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const processCallback = async () => {
      try {
        const code = searchParams.get('code');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Handle OAuth errors
        if (error) {
          setStatus('error');
          setErrorMessage(`Instagram authorization failed: ${error}`);
          return;
        }

        // Missing authorization code
        if (!code) {
          setStatus('error');
          setErrorMessage('No authorization code received from Instagram');
          return;
        }


        // Handle the callback
        const result = await handleCallback(code);

        setStatus('success');

        // Call success callback if provided
        // Check if user has Instagram Business accounts
        if (!result.instagramAccounts || result.instagramAccounts.length === 0) {
          setStatus('no-instagram');
          setErrorMessage('No Instagram Business accounts found. Please make sure you have a Facebook Page connected to an Instagram Business account.');
          return;
        }

        // Success callback
        if (onSuccess) {
          onSuccess(result);
        }

        // Redirect to profile setup or dashboard after short delay
        setTimeout(() => {
          navigate('/my-profile/profile', {
            state: {
              instagramData: result,
              step: 2 // Go to social media step with Instagram data
            }
          });
        }, 2000);

      } catch (error) {
        console.error('Instagram callback processing error:', error);
        setStatus('error');
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Failed to connect Instagram account'
        );
      }
    };

    processCallback();
  }, [searchParams, handleCallback, onSuccess, navigate]);

  const handleRetry = () => {
    navigate('/my-profile/profile');
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Spinner />
          <h2 className="mt-4 text-xl font-semibold text-gray-900">
            Connecting your Instagram account...
          </h2>
          <p className="mt-2 text-gray-600">
            Please wait while we fetch your Instagram data
          </p>
          <div className="mt-4 flex items-center justify-center space-x-2">
            <div className="animate-pulse flex space-x-2">
              <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
              <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
              <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4">
            <svg className="w-16 h-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Instagram Connected Successfully!
          </h2>
          <p className="text-gray-600 mb-4">
            Your Instagram professional account has been linked.
            We've automatically fetched your follower count and engagement data.
          </p>
          <div className="animate-pulse text-purple-600 font-medium">
            Redirecting to profile setup...
          </div>
        </div>
      </div>
    );
  }

  if (status === 'no-instagram') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-lg">
          <div className="w-16 h-16 mx-auto mb-4">
            <svg className="w-16 h-16 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No Instagram Business Account Found
          </h2>
          <p className="text-gray-600 mb-4">
            {errorMessage}
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Need help?</strong> To connect Instagram, you need a Facebook Page linked to an Instagram Business account.
              Visit your Instagram settings to switch to a Business account and connect it to a Facebook Page.
            </p>
          </div>
          <button
            onClick={handleRetry}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Profile Setup
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 mx-auto mb-4">
          <svg className="w-16 h-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Instagram Connection Failed
        </h2>
        <p className="text-gray-600 mb-4">
          {errorMessage}
        </p>
        <div className="space-y-3">
          <button
            onClick={handleRetry}
            className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Back to Profile Setup
          </button>
          <p className="text-sm text-gray-500">
            You can still set up your profile manually or try connecting Instagram again later.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InstagramCallback;
